import {
  BrandDefenseFilled,
  CategoryReviewFilled,
  formatData,
  WhosMyShopperFilled,
} from '@utilities';
import {
  COLOR_DEEPTEAL,
  COLOR_NUMERATOR_GREEN,
  COLOR_SAPPHIRE,
  COLOR_SCALE_0,
} from '@utilities/helpers/colors';
import { transformColumn } from '@api/fast_tracks/utilities/helpers';
import IndexBar from '../../Report/components/Tab/components/Visualizations/components/IndexBar';
import { fastTrackStatus } from '@helpers/fast_tracks';
import {
  HELP_CENTER_BASE_URL,
  HELP_CENTER_NARRATIVE_URLS,
} from '../../FastTracks/utilities/constants';

/**
 * Returns the default structure for topic data.
 * This includes default initial error state, visibility, loading state, and title.
 *
 * @function getDefaultTopicData
 * @param {Object} topic
 * @returns {Object} - Initial topic data structure
 */
export const getDefaultTopicData = (topic) => {
  return {
    error: false,
    isHidden: topic.isHidden,
    loading: true,
    tags: topic.tags,
    title: topic.title,
  };
};

/**
 * Maps data variant to specific colors
 * @param {string} variant - The data variant ('primary', 'secondary', 'other')
 * @returns {string} - The corresponding color hex code
 */
export const getColorByVariant = (variant) => {
  const colorMap = {
    other: COLOR_SAPPHIRE,
    primary: COLOR_NUMERATOR_GREEN,
    secondary: COLOR_DEEPTEAL,
  };

  return colorMap[variant?.toLowerCase()] || COLOR_SCALE_0;
};

/**
 * Transforms the data for the table
 * @param {Object} options
 * @param {Object[]} options.columnsData
 * @param {Object} options.rowData
 * @return {Object[]}
 */

export const getTableColumns = (columnsData) => {
  const columns = columnsData.map((col) => {
    const column = transformColumn(col);
    const hasIndexBar = column.indexCol;

    return {
      ...column,
      // eslint-disable-next-line react/prop-types
      Cell: ({ getValue }) => {
        const value = formatData({
          format: column.format,
          value: getValue() || 'NAN_SENTINEL',
        });

        if (!hasIndexBar) return value;

        return (
          <span
            style={{
              alignItems: 'center',
              display: 'flex',
            }}
          >
            {value}
            <IndexBar value={getValue()} />
          </span>
        );
      },
      width: hasIndexBar ? 200 : column.width,
    };
  });

  return columns;
};

/**
 * return the appropriate load header depending on the flow status
 *
 * @function loaderHeader
 * @param {string} flow status
 * @returns {string} the header of the load component
 */
export const loaderHeader = (status) => {
  switch (status) {
    case fastTrackStatus.QUEUED:
      return 'In Queue';
    case fastTrackStatus.SETUP:
      return 'Setup';
    default:
      return '';
  }
};

/**
 * return the appropriate load message depending on the flow status
 *
 * @function loaderMessage
 * @param {Flow} flow status
 * @returns {string} the message of the load component
 */
export const loaderMessage = (flow) => {
  if (!flow) {
    return [''];
  }

  switch (flow?.status) {
    case fastTrackStatus.SETUP:
      return ['Validating Sample'];
    default:
      return [''];
  }
};

/**
 * Returns the icon component based on the FastTrack ID
 * @param {string} fastTrackId - The ID of the FastTrack topic
 * @returns {React.Component|null} - The corresponding icon component or null if not found
 */
export const getIconByFastTrackId = (fastTrackId) => {
  const iconMap = {
    'brand-defense': <BrandDefenseFilled />,
    'brand-sell-in': <BrandDefenseFilled />,
    'category-review': <CategoryReviewFilled />,
    'who-is-my-shopper': <WhosMyShopperFilled />,
  };

  return iconMap[fastTrackId] || null;
};

/**
 * Returns structured data for different chart types to add to PowerPoint slides
 * @param {Object} dataset
 * @returns {Object}
 */
export const getPptStructuredData = (dataset) => {
  switch (dataset?.type) {
    case 'bar-chart':
    case 'bar-chart-two-labels':
    case 'column-chart':
    case 'bar-stack-chart':
      return {
        barDir: ['bar-chart', 'bar-chart-two-labels', 'bar-stack-chart'].includes(dataset.type)
          ? 'bar'
          : 'col',
        barGrouping: dataset.type === 'bar-stack-chart' ? 'percentStacked' : null,
        format: dataset?.yAxis?.labels?.format,
        series: dataset?.series,
        type: dataset?.type,
        xAxis: dataset?.xAxis,
      };
    case 'scatter-chart':
      return {
        formatX: dataset?.xAxis?.labels?.format,
        formatY: dataset?.yAxis?.labels?.format,
        nameX: dataset.xAxis?.title?.text || '',
        nameY: dataset?.yAxis?.title?.text || '',
        series: dataset?.series,
        type: dataset?.type,
        xAxis: dataset?.xAxis,
      };
    case 'table':
      return {
        columns: dataset?.columns,
        data: dataset?.data,
        type: dataset?.type,
      };
    case 'brand-tree-v2':
      return {
        type: 'brand-tree-v2',
      };
    default:
      // Only suitable for highchart components
      return {
        type: 'chart-image',
      };
  }
};

/**
 * Returns the help article URL based on the FastTrack ID
 * @param {string} fastTrackId
 * @returns {string}
 */
export const getHelpArticleURLByFastTrackId = (fastTrackId) => {
  return HELP_CENTER_NARRATIVE_URLS[fastTrackId] || HELP_CENTER_BASE_URL;
};
